/**
 * RepBuddy - Stripe billing client (SCRUM-46)
 *
 * The browser's side of the four billing endpoints. Everything a user can do with money
 * goes through here, and nothing here decides anything: prices come from the server
 * (resolved against the caller's own price book) and entitlement comes from the webhook.
 *
 * Why the client never sends a price: it sends an ITEM KEY ('solo_monthly') and the backend
 * resolves it against that user's price book. A founding member gets founding rates because
 * of who they are, not because of what their browser asked for, so there is no request
 * anyone can craft to reach a discounted price. The catalogue response deliberately omits
 * Stripe price ids for the same reason.
 */

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

/** Item keys, mirroring price_book_items.item_key on the server. */
export const ITEMS = {
  soloMonthly: 'solo_monthly',
  soloYearly:  'solo_yearly',
  proMonthly:  'pro_monthly',
  proYearly:   'pro_yearly',
  leads50:     'leads_50',
  verify500:   'verify_500',
  verify2000:  'verify_2000',
};

/** itemKey for a plan + billing cycle, as the UI names them. */
export function itemKeyFor(plan, billing) {
  const p = String(plan || '').toLowerCase() === 'pro' ? 'pro' : 'solo';
  const yearly = /^(annual|yearly|year)$/i.test(String(billing || ''));
  return `${p}_${yearly ? 'yearly' : 'monthly'}`;
}

function authHeaders(token) {
  return token ? { Authorization: `Bearer ${token}` } : {};
}

/**
 * The caller's price list. Public and unauthenticated, so the marketing pricing page can
 * render it; pass a token and a founding member gets their locked rates instead.
 *
 * Returns { available, priceBook: { key, label, lifetimeLock }, items: { [itemKey]: {...} } }.
 * On failure returns available:false and the caller falls back to its static copy, because
 * a pricing page that renders nothing is worse than one that is briefly stale.
 */
export async function fetchCatalogue({ token } = {}) {
  try {
    const res = await fetch(`${BACKEND_URL}/api/billing/catalogue`, { headers: authHeaders(token) });
    if (!res.ok) return { available: false, items: {} };
    const d = await res.json();
    return {
      available: !!d.available,
      priceBook: d.priceBook || { key: 'public', label: 'Standard', lifetimeLock: false },
      items: d.items || {},
    };
  } catch {
    return { available: false, items: {} };
  }
}

/** The live subscription + meters for the Account page. */
export async function fetchSubscription({ token } = {}) {
  if (!token) return { available: false };
  try {
    const res = await fetch(`${BACKEND_URL}/api/billing/subscription`, { headers: authHeaders(token) });
    if (!res.ok) return { available: false };
    return await res.json();
  } catch {
    return { available: false };
  }
}

/**
 * Open Stripe Checkout for an item. Resolves to the hosted URL; the caller navigates.
 *
 * Throws an Error carrying `code` for the two refusals the UI must handle differently
 * from a generic failure:
 *   no_subscription     a booster needs an active plan first (the product copy says so)
 *   already_subscribed  plan changes belong in the Portal, where Stripe prorates. A second
 *                       Checkout would create a second subscription and bill twice.
 */
export async function startCheckout(itemKey, { token } = {}) {
  const res = await fetch(`${BACKEND_URL}/api/billing/checkout`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders(token) },
    body: JSON.stringify({ itemKey }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || !data.url) {
    const err = new Error(data.error || 'Could not start checkout');
    err.code = data.code || null;
    err.status = res.status;
    throw err;
  }
  return data.url;
}

/**
 * Open the Stripe Customer Portal: plan changes, card updates, cancel/resume, invoices.
 * The configuration is chosen server-side by price book, so a founding member is only ever
 * offered founding prices.
 */
export async function openPortal({ token } = {}) {
  const res = await fetch(`${BACKEND_URL}/api/billing/portal`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders(token) },
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || !data.url) {
    const err = new Error(data.error || 'Could not open the billing portal');
    err.code = data.code || null;
    err.status = res.status;
    throw err;
  }
  return data.url;
}

/**
 * Recover entitlement straight from Stripe.
 *
 * The webhook is the fast path, not the only one. It fails for reasons that have nothing to
 * do with the buyer (a stale DNS record, a certificate that does not cover the host, a
 * backend that is briefly down), and the result is someone who has paid staring at the
 * paywall. This asks the server to read their live subscription from Stripe and apply it.
 *
 * Deliberately does NOT throw. Every caller is a user-facing recovery path, and a failure
 * here should degrade to "still no access, try again" rather than break the screen that is
 * trying to rescue them.
 */
export async function syncBilling({ token } = {}) {
  try {
    const res = await fetch(`${BACKEND_URL}/api/billing/sync`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders(token) },
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) return { ok: false, entitled: false };
    return data;
  } catch {
    return { ok: false, entitled: false };
  }
}

/**
 * Send the browser to a Stripe-hosted page. A full navigation, not a new tab: Checkout and
 * the Portal both return here afterwards, and a popup would be blocked as often as not.
 */
export function redirectTo(url) {
  window.location.assign(url);
}

/** Convenience: start checkout and go, or throw for the caller to render. */
export async function checkoutAndRedirect(itemKey, { token } = {}) {
  redirectTo(await startCheckout(itemKey, { token }));
}

/** Convenience: open the portal and go, or throw for the caller to render. */
export async function portalAndRedirect({ token } = {}) {
  redirectTo(await openPortal({ token }));
}

// ─── Display helpers ──────────────────────────────────────────────────────────

/** `$59`, or `$1,190`. Whole dollars, since every price in the catalogue is one. */
export function formatCents(cents) {
  if (typeof cents !== 'number') return '-';
  const dollars = cents / 100;
  return `$${(Number.isInteger(dollars) ? dollars : dollars.toFixed(2)).toLocaleString('en-US')}`;
}

/**
 * What to show under a yearly price. Derived from the two real amounts rather than a
 * hardcoded claim, because the hardcoded one was wrong: the products said "2 months free"
 * while public yearly is nearer 31% off, and only the founding yearly prices are literally
 * two months free.
 */
export function yearlySavingNote(monthlyCents, yearlyCents) {
  if (!monthlyCents || !yearlyCents) return null;
  const full = monthlyCents * 12;
  if (yearlyCents >= full) return null;
  const pct = Math.round((1 - yearlyCents / full) * 100);
  // FLOOR, not round. Public yearly is 3.69 months free and Math.round turned that into a
  // claim of "4 months free", ie promising more discount than the price actually gives.
  // Understating is harmless; overstating is a false advertisement on a checkout button.
  const monthsFree = Math.floor((full - yearlyCents) / monthlyCents);
  return monthsFree >= 2 ? `save ${pct}% (${monthsFree} months free)` : `save ${pct}%`;
}
